/**
 * ReservationsToolbar — buscar y acotar.
 *
 * Los filtros rápidos están en el idioma del operador ("Pendiente de cobro",
 * no `payment_status=unpaid`). Los filtros finos viven en una fila plegable en
 * escritorio y en una hoja inferior en móvil, para que la pantalla arranque
 * con la lista y no con un panel de controles.
 */
import React, { useEffect, useState } from "react";
import { MagnifyingGlass, SlidersHorizontal, X } from "@phosphor-icons/react";
import { acquireBodyScrollLock } from "@/lib/bodyScrollLock";
import { CHANNELS, BOOKING_STATUS_LABEL, STAY_STATUS_LABEL, PAYMENT_STATUS_LABEL } from "@/data/pmsDemoData";
import { QUICK_FILTERS } from "@/data/reservationsV2Demo";
import { SURFACE } from "@/pages/admin/pms/v2/V2Primitives";

const ALL = { value: "all", label: "Todos" };

export default function ReservationsToolbar({
  query, onQuery, quick, onQuick, counts, filters, onFilters, settings, unitLabel, desktop,
}) {
  const [open, setOpen] = useState(false);
  const activeFilters = Object.entries(filters).filter(([, v]) => v && v !== "all").length;

  const fields = buildFields({ settings, unitLabel });

  return (
    <div className="mb-4" data-testid="reservations-toolbar">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Huésped, referencia, unidad, email o teléfono…"
            aria-label="Buscar reservas"
            data-testid="reservations-search"
            className="dv-input w-full pl-9 min-h-[44px] text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="reservations-filters-toggle"
          className={`${SURFACE} shrink-0 min-h-[44px] px-3 inline-flex items-center gap-1.5 text-xs text-stone-600 dark:text-stone-300 hover:border-stone-400`}
        >
          <SlidersHorizontal size={14} />
          Filtros
          {activeFilters > 0 && (
            <span className="tabular-nums text-[10px] bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 rounded-full px-1.5">
              {activeFilters}
            </span>
          )}
        </button>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="reservations-quick-filters">
        {QUICK_FILTERS.map((f) => {
          const on = quick === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => onQuick(on ? null : f.key)}
              aria-pressed={on}
              data-testid={`reservations-quick-${f.key}`}
              className={`min-h-[44px] px-3 rounded-full text-xs border transition-colors ${
                on
                  ? "bg-stone-900 text-white border-stone-900 dark:bg-stone-100 dark:text-stone-900 dark:border-stone-100"
                  : "bg-white text-stone-600 border-stone-300 hover:border-stone-500 dark:bg-stone-950 dark:text-stone-400 dark:border-stone-700"
              }`}
            >
              {f.label}
              <span className="ml-1.5 tabular-nums opacity-60">{counts[f.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {open && (desktop
        ? (
          <div className={`${SURFACE} mt-2 p-3 grid grid-cols-2 xl:grid-cols-3 gap-3`} data-testid="reservations-filters-panel">
            <FilterFields fields={fields} filters={filters} onFilters={onFilters} />
          </div>
        )
        : <FiltersSheet fields={fields} filters={filters} onFilters={onFilters} onClose={() => setOpen(false)} />)}
    </div>
  );
}

function buildFields({ settings, unitLabel }) {
  return [
    {
      key: "booking_status",
      label: "Estado de la reserva",
      options: [ALL, ...Object.entries(BOOKING_STATUS_LABEL).map(([value, label]) => ({ value, label }))],
    },
    {
      key: "stay_status",
      label: "Estancia",
      options: [ALL, ...Object.entries(STAY_STATUS_LABEL).map(([value, label]) => ({ value, label }))],
    },
    {
      key: "payment_status",
      label: "Cobro",
      options: [ALL, ...Object.entries(PAYMENT_STATUS_LABEL).map(([value, label]) => ({ value, label }))],
    },
    {
      key: "channel",
      label: "Canal",
      options: [ALL, ...Object.values(CHANNELS).map((c) => ({ value: c.key, label: c.label }))],
    },
    {
      key: "unit_type",
      label: "Tipo de alojamiento",
      options: [ALL, ...settings.unitTypes.map((t) => ({ value: t.unit_type_id, label: t.name }))],
    },
    {
      key: "unit",
      label: `${unitLabel.singular} asignada`,
      options: [
        ALL,
        { value: "none", label: "Sin asignar" },
        ...settings.units.map((u) => ({ value: u.unit_id, label: `${u.unit_id} · ${u.name}` })),
      ],
    },
    { key: "from", label: "Desde", type: "date" },
    { key: "to", label: "Hasta", type: "date" },
  ];
}

function FilterFields({ fields, filters, onFilters }) {
  return (
    <>
      {fields.map((f) => (
        <label key={f.key} className="block min-w-0">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
            {f.label}
          </span>
          {f.type === "date" ? (
            <input
              type="date"
              value={filters[f.key] || ""}
              onChange={(e) => onFilters({ ...filters, [f.key]: e.target.value })}
              data-testid={`reservations-filter-${f.key}`}
              className="dv-input w-full text-xs min-h-[44px]"
            />
          ) : (
            <select
              value={filters[f.key] || "all"}
              onChange={(e) => onFilters({ ...filters, [f.key]: e.target.value })}
              data-testid={`reservations-filter-${f.key}`}
              className="dv-input w-full text-xs min-h-[44px]"
            >
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </label>
      ))}
    </>
  );
}

/** Misma cortesía que el resto de hojas: Escape cierra y el fondo no hace scroll. */
function FiltersSheet({ fields, filters, onFilters, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const release = acquireBodyScrollLock();
    return () => {
      document.removeEventListener("keydown", onKey);
      release();
    };
  }, [onClose]);

  return (
    <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="reservations-filters-sheet">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-stone-950 border-t border-stone-200 dark:border-stone-800 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))]">
        <div className="sticky top-0 bg-white dark:bg-stone-950 px-4 pt-4 pb-3 flex items-center justify-between border-b border-stone-100 dark:border-stone-800">
          <div className="font-serif-dv text-lg">Filtros</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="reservations-filters-sheet-close"
            className="w-11 h-11 -mr-2 flex items-center justify-center text-stone-500"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 gap-3">
          <FilterFields fields={fields} filters={filters} onFilters={onFilters} />
        </div>
      </div>
    </div>
  );
}
